import React, { memo, useState, useEffect, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Box, Typography } from '@material-ui/core'

import styles from '../styles'
import { combineStyles, PlaceholderContent } from 'utils'
import { IPublicCompany } from 'models'
import { Loading } from 'components'
import commonStyles from '../../../styles/common'
import { useCompanyQuery } from 'graphql/__generated__/graphql'
import { useApolloClient } from '@apollo/react-hooks'
import { PublicCompanyQuery } from 'store'

const CompanyGallery: React.FunctionComponent<any> = () => {
	const [publicCompany, setPublicCompany] = useState<IPublicCompany>()
	const client = useApolloClient()
	const { companySlug }: { companySlug?: string } = useParams()
	const combinedStyles: any = combineStyles(commonStyles, styles)
	const useStyles = makeStyles(combinedStyles)
	const classes = useStyles()
	const { t } = useTranslation()

	const { data: companyData } = useCompanyQuery({
		variables: {
			slug: '%' + companySlug + '%',
		},
	})

	const company = useMemo(
		() =>
			(companyData &&
				companyData?.companyCollection?.edges?.map(x => x.node))?.[0] ||
			null,
		[companyData],
	)

	useEffect(() => {
		if (company?.name)
			client
				.query({
					query: PublicCompanyQuery,
					variables: {
						name: company?.name,
					},
				})
				.then((companyResponse: any) => {
					setPublicCompany(
						companyResponse.data.public_companiesCollection.edges[0]
							.node,
					)
				})
	}, [client, company])

	const galleryItems =
		publicCompany?.company_public_galleriesCollection?.edges?.map(
			(edge: any) => edge.node,
		) || []

	return (
		<Grid
			className={classes.section}
			container={true}
			direction="column"
			style={{ marginTop: 30 }}
		>
			<Grid item={true}>
				<h2
					className={classes.sectionTitle}
					style={{
						display: 'flex',
						flexDirection: 'row',
						marginBottom: 20,
					}}
				>
					Company&nbsp;
					<span className={classes.emphasizedText}>
						{t('Gallery')}
					</span>
				</h2>
			</Grid>

			{publicCompany === undefined ? (
				<Loading size={48} style={{ margin: '100px auto' }} />
			) : (
				<Grid container={true} spacing={2}>
					{galleryItems && galleryItems.length > 0 ? (
						galleryItems.map((item: any, index: number) => (
							<Grid
								item
								xs={12}
								sm={6}
								md={4}
								key={item.id || index}
							>
								<Box
									style={{
										width: '100%',
										paddingTop: '75%',
										position: 'relative',
										backgroundColor: '#f5f5f5',
										borderRadius: 8,
										overflow: 'hidden',
										cursor: 'pointer',
									}}
								>
									<img
										src={`${
											process.env.REACT_APP_R2_PATH ?? ''
										}${item.path}`}
										alt={
											item.text ||
											item.name ||
											`Gallery image ${index + 1}`
										}
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											height: '100%',
											objectFit: 'cover',
										}}
									/>
								</Box>
								{item.text && (
									<Typography
										variant="caption"
										display="block"
										style={{ marginTop: 8 }}
									>
										{item.text}
									</Typography>
								)}
							</Grid>
						))
					) : (
						<Grid item xs={12}>
							<PlaceholderContent
								data={publicCompany?.name || t('the company')}
								entity={'gallery'}
							/>
						</Grid>
					)}
				</Grid>
			)}
		</Grid>
	)
}

export default memo(CompanyGallery)
